<template>
  <PublicLayout page-title="Información RGPD">
    <div class="max-w-6xl mx-auto p-4 md:p-8">
      <!-- Page Header -->
      <div class="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 mb-8">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div class="min-w-0 flex-1">
            <!-- Page Title -->
            <div class="flex items-start gap-4">
              <div class="min-w-0 flex-1">
                <h1 class="text-3xl md:text-4xl font-bold text-slate-900 mb-2 tracking-tight">
                  Información RGPD
                </h1>
                <p class="text-lg text-slate-600 max-w-2xl leading-relaxed">
                  Todo lo que necesitas saber sobre el Reglamento General de Protección de Datos y tus derechos
                </p>
              </div>
            </div>
          </div>

          <!-- Logo/Brand -->
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-500 rounded-lg flex items-center justify-center shadow-lg">
              <span class="text-white font-bold text-lg">O</span>
            </div>
            <span class="text-slate-900 font-bold text-xl">Olvidadme</span>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="space-y-8">
        <!-- ¿Qué es el RGPD? Section -->
        <section class="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <h2 class="text-3xl font-bold text-slate-900 mb-6 tracking-tight flex items-center">
            <ShieldCheckIcon class="w-6 h-6 text-teal-600 mr-3" />
            ¿Qué es el RGPD?
          </h2>
          
          <div class="space-y-4 text-slate-600 leading-relaxed">
            <p>
              El <strong>Reglamento General de Protección de Datos (RGPD)</strong> es la normativa europea que protege tu privacidad y te otorga el control sobre tu información personal. Entre otros, reconoce derechos como el acceso, la rectificación, la supresión —conocida como derecho al olvido— y la portabilidad de tus datos. En Olvidadme, utilizamos estas herramientas legales para ayudarte a recuperar el control de tu huella digital y proteger tu reputación online.
            </p>
            
            <div class="bg-teal-50 border border-teal-200 rounded-lg p-6 my-6">
              <h3 class="font-semibold text-teal-900 mb-3 text-lg">Objetivos principales del RGPD:</h3>
              <ul class="space-y-2">
                <li class="flex items-start">
                  <CheckCircleIcon class="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Devolver a los ciudadanos el control sobre sus datos personales</span>
                </li>
                <li class="flex items-start">
                  <CheckCircleIcon class="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Simplificar el entorno regulador para las empresas</span>
                </li>
                <li class="flex items-start">
                  <CheckCircleIcon class="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Aumentar la protección de los datos en la era digital</span>
                </li>
              </ul>
            </div>

            <p>
              El RGPD se aplica a todas las organizaciones que procesan datos personales de residentes de la UE, 
              independientemente de dónde tenga su sede la organización. Esto significa que empresas de todo el mundo 
              deben cumplir con estas regulaciones si manejan datos de ciudadanos europeos.
            </p>
          </div>
        </section>

        <!-- Tus Derechos RGPD Section -->
        <section class="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <h2 class="text-3xl font-bold text-slate-900 mb-6 tracking-tight flex items-center">
            <ScaleIcon class="w-6 h-6 text-teal-600 mr-3" />
            Tus Derechos bajo el RGPD
          </h2>
          
          <div class="grid gap-6 md:grid-cols-2">
            <!-- Derecho de Acceso -->
            <div class="bg-white rounded-lg border border-slate-200 p-6 hover:transform hover:scale-[1.02] hover:shadow-lg transition-all duration-200">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <DocumentTextIcon class="w-5 h-5 text-white" />
                </div>
                <h3 class="font-semibold text-slate-900 text-lg">Derecho de Acceso</h3>
              </div>
              <p class="text-slate-600">
                Tienes derecho a obtener confirmación sobre si se están tratando tus datos personales y, 
                en caso afirmativo, acceder a dichos datos y obtener una copia de los mismos.
              </p>
            </div>

            <!-- Derecho de Rectificación -->
            <div class="bg-white rounded-lg border border-slate-200 p-6 hover:transform hover:scale-[1.02] hover:shadow-lg transition-all duration-200">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 bg-gradient-to-r from-green-600 to-green-500 rounded-lg flex items-center justify-center mr-3">
                  <PencilSquareIcon class="w-5 h-5 text-white" />
                </div>
                <h3 class="font-semibold text-slate-900 text-lg">Derecho de Rectificación</h3>
              </div>
              <p class="text-slate-600">
                Puedes solicitar la corrección de tus datos personales inexactos o incompletos. 
                Las organizaciones deben responder sin dilación indebida.
              </p>
            </div>

            <!-- Derecho al Olvido -->
            <div class="bg-white rounded-lg border border-slate-200 p-6 hover:transform hover:scale-[1.02] hover:shadow-lg transition-all duration-200">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 bg-gradient-to-r from-red-600 to-red-500 rounded-lg flex items-center justify-center mr-3">
                  <TrashIcon class="w-5 h-5 text-white" />
                </div>
                <h3 class="font-semibold text-slate-900 text-lg">Derecho al Olvido (Supresión)</h3>
              </div>
              <p class="text-slate-600">
                Puedes solicitar la eliminación de tus datos personales cuando ya no sean necesarios, 
                retires el consentimiento o el tratamiento sea ilícito. <strong>Este es nuestro servicio principal.</strong>
              </p>
            </div>

            <!-- Derecho de Portabilidad -->
            <div class="bg-white rounded-lg border border-slate-200 p-6 hover:transform hover:scale-[1.02] hover:shadow-lg transition-all duration-200">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <ArrowsRightLeftIcon class="w-5 h-5 text-white" />
                </div>
                <h3 class="font-semibold text-slate-900 text-lg">Derecho de Portabilidad</h3>
              </div>
              <p class="text-slate-600">
                Tienes derecho a recibir tus datos en un formato estructurado, de uso común y lectura mecánica, 
                y a transmitirlos a otro responsable.
              </p>
            </div>

            <!-- Derecho de Oposición -->
            <div class="bg-white rounded-lg border border-slate-200 p-6 hover:transform hover:scale-[1.02] hover:shadow-lg transition-all duration-200">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg flex items-center justify-center mr-3">
                  <HandRaisedIcon class="w-5 h-5 text-white" />
                </div>
                <h3 class="font-semibold text-slate-900 text-lg">Derecho de Oposición</h3>
              </div>
              <p class="text-slate-600">
                Puedes oponerte al tratamiento de tus datos personales para fines de marketing directo, 
                investigación científica o histórica, o estadística.
              </p>
            </div>

            <!-- Derecho de Limitación -->
            <div class="bg-white rounded-lg border border-slate-200 p-6 hover:transform hover:scale-[1.02] hover:shadow-lg transition-all duration-200">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                  <LockClosedIcon class="w-5 h-5 text-white" />
                </div>
                <h3 class="font-semibold text-slate-900 text-lg">Derecho de Limitación</h3>
              </div>
              <p class="text-slate-600">
                Puedes solicitar la limitación del tratamiento de tus datos cuando impugnes su exactitud, 
                el tratamiento sea ilícito o ya no se necesiten pero los requieras para reclamaciones.
              </p>
            </div>
          </div>
        </section>

        <!-- Plazos y Procedimientos Section -->
        <section class="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <h2 class="text-3xl font-bold text-slate-900 mb-6 tracking-tight flex items-center">
            <ClockIcon class="w-6 h-6 text-teal-600 mr-3" />
            Plazos y Procedimientos
          </h2>
          
          <div class="space-y-6">
            <div class="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h3 class="font-semibold text-amber-900 mb-3 text-lg flex items-center">
                <ExclamationTriangleIcon class="w-5 h-5 mr-2" />
                Plazos Legales Importantes
              </h3>
              <ul class="space-y-3 text-slate-700">
                <li class="flex items-start">
                  <span class="font-semibold mr-2">Respuesta inicial:</span>
                  <span>1 mes</span>
                </li>
                <li class="flex items-start">
                  <span class="font-semibold mr-2">Ampliación posible:</span>
                  <span>Hasta 2 meses más, con aviso previo</span>
                </li>
                <li class="flex items-start">
                  <span class="font-semibold mr-2">Falta de respuesta o denegación:</span>
                  <span>Posible reclamación ante la AEPD</span>
                </li>
              </ul>
            </div>

            <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 class="font-semibold text-blue-900 mb-3 text-lg">Procedimiento para Ejercer tus Derechos</h3>
              <ol class="space-y-3 text-slate-700">
                <li class="flex items-start">
                  <span class="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">1</span>
                  <div>
                    <strong>Identificación:</strong> Debes acreditar tu identidad (DNI, NIE o pasaporte)
                  </div>
                </li>
                <li class="flex items-start">
                  <span class="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">2</span>
                  <div>
                    <strong>Solicitud clara:</strong> Especifica qué derecho quieres ejercer y sobre qué datos
                  </div>
                </li>
                <li class="flex items-start">
                  <span class="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">3</span>
                  <div>
                    <strong>Envío:</strong> Remite la solicitud al responsable del tratamiento (nosotros podemos hacerlo por ti)
                  </div>
                </li>
                <li class="flex items-start">
                  <span class="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">4</span>
                  <div>
                    <strong>Seguimiento:</strong> Si no recibes respuesta o es negativa, puedes reclamar ante la AEPD
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </section>

        <!-- Cómo te Ayudamos Section -->
        <section class="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl shadow-xl border border-teal-200 p-8">
          <h2 class="text-3xl font-bold text-slate-900 mb-6 tracking-tight flex items-center">
            <SparklesIcon class="w-6 h-6 text-teal-600 mr-3" />
            Cómo Olvidadme te ayuda con el RGPD
          </h2>
          
          <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div class="bg-white/80 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <CheckBadgeIcon class="w-8 h-8 text-teal-600 mb-3" />
              <h3 class="font-semibold text-slate-900 mb-2">Gestión Integral</h3>
              <p class="text-slate-600 text-sm">
                Nos encargamos de todo el proceso legal, desde la identificación hasta el seguimiento
              </p>
            </div>
            
            <div class="bg-white/80 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <DocumentDuplicateIcon class="w-8 h-8 text-teal-600 mb-3" />
              <h3 class="font-semibold text-slate-900 mb-2">Documentación Legal</h3>
              <p class="text-slate-600 text-sm">
                Preparamos todos los documentos legales necesarios con validez jurídica
              </p>
            </div>
            
            <div class="bg-white/80 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <UserGroupIcon class="w-8 h-8 text-teal-600 mb-3" />
              <h3 class="font-semibold text-slate-900 mb-2">Abogados Especializados</h3>
              <p class="text-slate-600 text-sm">
                Equipo legal experto en protección de datos y derecho digital
              </p>
            </div>
            
            <div class="bg-white/80 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <BellAlertIcon class="w-8 h-8 text-teal-600 mb-3" />
              <h3 class="font-semibold text-slate-900 mb-2">Seguimiento Activo</h3>
              <p class="text-slate-600 text-sm">
                Monitorizamos cada caso hasta su resolución completa
              </p>
            </div>
            
            <div class="bg-white/80 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <BuildingLibraryIcon class="w-8 h-8 text-teal-600 mb-3" />
              <h3 class="font-semibold text-slate-900 mb-2">Reclamación ante AEPD</h3>
              <p class="text-slate-600 text-sm">
                Si es necesario, elevamos el caso a la Agencia de Protección de Datos
              </p>
            </div>
            
            <div class="bg-white/80 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <ShieldExclamationIcon class="w-8 h-8 text-teal-600 mb-3" />
              <h3 class="font-semibold text-slate-900 mb-2">Gestión profesional</h3>
              <p class="text-slate-600 text-sm">
                En todos nuestros servicios garantizamos gestión profesional hasta resolución
              </p>
            </div>
          </div>
        </section>

        <!-- Enlaces a páginas GDPR Section -->
        <section class="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <h2 class="text-3xl font-bold text-slate-900 mb-6 tracking-tight">
            Gestiona tus Derechos GDPR
          </h2>
          
          <div class="grid gap-4 md:grid-cols-2">
            <router-link 
              to="/gdpr/consent" 
              class="flex items-center justify-between p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            >
              <div class="flex items-center">
                <ClipboardDocumentCheckIcon class="w-6 h-6 text-teal-600 mr-3" />
                <div>
                  <h3 class="font-semibold text-slate-900">Gestión de consentimientos</h3>
                  <p class="text-sm text-slate-600">Revisa y gestiona tus consentimientos</p>
                </div>
              </div>
              <ChevronRightIcon class="w-5 h-5 text-teal-600" />
            </router-link>
            
            <router-link 
              to="/gdpr/data" 
              class="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            >
              <div class="flex items-center">
                <EyeIcon class="w-6 h-6 text-blue-600 mr-3" />
                <div>
                  <h3 class="font-semibold text-slate-900">Ver mis datos</h3>
                  <p class="text-sm text-slate-600">Accede a toda tu información personal</p>
                </div>
              </div>
              <ChevronRightIcon class="w-5 h-5 text-blue-600" />
            </router-link>
            
            <router-link 
              to="/gdpr/export" 
              class="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            >
              <div class="flex items-center">
                <ArrowDownTrayIcon class="w-6 h-6 text-purple-600 mr-3" />
                <div>
                  <h3 class="font-semibold text-slate-900">Exportar datos</h3>
                  <p class="text-sm text-slate-600">Descarga todos tus datos personales</p>
                </div>
              </div>
              <ChevronRightIcon class="w-5 h-5 text-purple-600" />
            </router-link>
            
            <router-link 
              to="/gdpr/delete" 
              class="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            >
              <div class="flex items-center">
                <ExclamationCircleIcon class="w-6 h-6 text-red-600 mr-3" />
                <div>
                  <h3 class="font-semibold text-slate-900">Eliminar cuenta</h3>
                  <p class="text-sm text-slate-600">Solicita la eliminación completa</p>
                </div>
              </div>
              <ChevronRightIcon class="w-5 h-5 text-red-600" />
            </router-link>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl shadow-xl p-8 text-white text-center">
          <h2 class="text-3xl font-bold mb-4">¿Necesitas Ayuda con tus Derechos RGPD?</h2>
          <p class="text-lg mb-8 text-teal-50 max-w-2xl mx-auto">
            Nuestro equipo de expertos legales está listo para ayudarte a ejercer tus derechos 
            de protección de datos de forma efectiva y profesional.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link 
              to="/auth/register" 
              class="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-semibold rounded-lg text-teal-600 bg-white hover:bg-teal-50 transition-all duration-200 transform hover:scale-[1.02] shadow-lg"
            >
              Comenzar Ahora
            </router-link>
            <router-link 
              to="/support/contact" 
              class="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-semibold rounded-lg text-white hover:bg-white/10 transition-all duration-200 transform hover:scale-[1.02]"
            >
              Contactar con un Experto
            </router-link>
          </div>
        </section>
      </div>
    </div>
  </PublicLayout>
</template>

<script setup>
import { onMounted } from 'vue'
import { useSeo } from '@/composables/useSeo'
import PublicLayout from '@/components/layout/PublicLayout.vue'

// SEO Meta Tags
useSeo({
  title: 'Guía RGPD - Tus Derechos',
  description: 'Todo sobre el Reglamento de Protección de Datos y cómo ejercer tus derechos.',
  canonical: '/legal/rgpd-info'
})
import { 
  ShieldCheckIcon,
  ScaleIcon,
  ClockIcon,
  SparklesIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowsRightLeftIcon,
  HandRaisedIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  BellAlertIcon,
  BuildingLibraryIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  ChevronRightIcon
} from '@heroicons/vue/24/outline'

/**
 * RGPD Information Page Component
 * 
 * Comprehensive information about GDPR rights and procedures
 * Educational content following legal requirements
 * Links to GDPR management pages
 * 
 * Features:
 * - Complete GDPR rights explanation
 * - Legal timelines and procedures
 * - How Olvidadme helps with GDPR
 * - Links to GDPR management tools
 * - Call-to-action for services
 */

onMounted(() => {
  // Scroll to top when page loads
  window.scrollTo({ top: 0, behavior: 'smooth' })
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Custom hover effects for interactive elements */
.hover\:scale-\[1\.02\]:hover {
  transform: scale(1.02);
}
</style>